import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import {
  keycloak,
  keycloakInitOptions,
  TOKEN_MIN_VALIDITY_SECONDS,
  TOKEN_REFRESH_INTERVAL_MS
} from '../config/keycloak';
import { validateUser, validateUserService } from '../api/user';
import { User } from '../@types/users/user';
import { useAppContext } from './AppContext';

interface AuthContextProps {
  accessToken: string | null;
  currentUser: User | null;
  username: string | null;
  handleLogin: (username?: string, password?: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const TOKEN_KEY = 'access_token';

const GLOBAL_INIT_FLAG = '__geowellexKeycloakInitialized';

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { updateConfigStates, updateData } = useAppContext();

  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [username, setUsername] = useState<string | null>(null);
  const refreshTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      const flag = keycloak as unknown as Record<string, boolean>;
      if (flag[GLOBAL_INIT_FLAG]) {
        setAccessToken(keycloak.token ?? null);
        return;
      }

      try {
        const authenticated = await keycloak.init(keycloakInitOptions);
        flag[GLOBAL_INIT_FLAG] = true;

        if (cancelled) return;

        if (authenticated && keycloak.token) {
          setAccessToken(keycloak.token);
          try {
            const info = (await keycloak.loadUserInfo()) as Record<string, unknown>;
            const preferred = info?.preferred_username as string | undefined;
            const email = info?.email as string | undefined;
            setUsername(preferred ?? email ?? null);
          } catch (error) {
            console.error('Failed to load Keycloak user info', error);
          }

          refreshTimer.current = setInterval(() => {
            keycloak
              .updateToken(TOKEN_MIN_VALIDITY_SECONDS)
              .then((refreshed) => {
                if (refreshed && keycloak.token) {
                  setAccessToken(keycloak.token);
                }
              })
              .catch((error) => {
                console.error('Token refresh failed', error);
              });
          }, TOKEN_REFRESH_INTERVAL_MS);
        }
      } catch (error) {
        console.error('Keycloak init failed', error);
      }
    };

    init();

    return () => {
      cancelled = true;
      if (refreshTimer.current) {
        clearInterval(refreshTimer.current);
        refreshTimer.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (accessToken) {
      localStorage.setItem(TOKEN_KEY, accessToken);
    } else {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem('user');
      localStorage.removeItem('username');
    }
  }, [accessToken]);

  useEffect(() => {
    if (username) localStorage.setItem('username', username);
  }, [username]);

  useEffect(() => {
    let cancelled = false;

    const enforceAccess = async () => {
      if (!accessToken) {
        setCurrentUser(null);
        return;
      }
      try {
        const user = await validateUser(accessToken);
        const hasPermission = await validateUserService(accessToken);
        if (cancelled) return;

        if (user && hasPermission) {
          setCurrentUser(user);
          localStorage.setItem('user', JSON.stringify(user));
          updateConfigStates({ fetchProjects: true });
          updateData({ projects: [], projectSamples: [], wells: [] });
        } else {
          setCurrentUser(null);
          localStorage.removeItem('user');
        }
      } catch (error) {
        if (!cancelled) {
          console.error('User validation failed', error);
          setCurrentUser(null);
          localStorage.removeItem('user');
        }
      }
    };

    enforceAccess();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  const handleLogin = useCallback(async () => {
    await keycloak.login();
  }, []);

  const logout = useCallback(async () => {
    if (refreshTimer.current) {
      clearInterval(refreshTimer.current);
      refreshTimer.current = null;
    }
    setAccessToken(null);
    setCurrentUser(null);
    setUsername(null);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem('user');
    localStorage.removeItem('username');
    try {
      await keycloak.logout({ redirectUri: window.location.origin + '/login' });
    } catch (error) {
      console.error('Keycloak logout failed', error);
    }
  }, []);

  const value = useMemo<AuthContextProps>(
    () => ({
      accessToken,
      currentUser,
      username,
      handleLogin,
      logout
    }),
    [accessToken, currentUser, username, handleLogin, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextProps => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
